import React from 'react';
import styled from '@emotion/styled';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import { keyForString } from 'services/translation';
import { Theme } from 'ui_system/theme';
import sanitizeHtml from 'sanitize-html';

const HeaderStyle = styled.h5`
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
`;

const ContentStyle = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  text-align: left;
`;

const AdditionalInfoButton = styled.a`
  display: inline-block;
  background-color: ${Theme.colors.linksDesignElementsBaseColor};
  color: ${Theme.colors.white};
  border: transparent;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  padding: 13px 32px;
  text-align: center;

  &:hover {
    color: ${Theme.colors.white};
  }
`;

const ScreenReader = styled.span`
  display: none;
`;

const additionalGuestMessages = defineMessages({
  BEFORE_YOU_ARRIVE: { defaultMessage: 'Before You Arrive' },
  COMPLETE_ADDITIONAL_INFORMATION: {
    defaultMessage: 'Complete additional information',
  },
});

type AdditionalGuestMessageKey = keyof typeof additionalGuestMessages;

const translateProp = (
  intl: ReturnType<typeof useIntl>,
  value: string | null
): string | null => {
  if (!value) return null;
  const key = keyForString(value) as AdditionalGuestMessageKey;
  if (key in additionalGuestMessages) {
    return intl.formatMessage(additionalGuestMessages[key]);
  }
  return value;
};

type AdditionalGuestsProps = {
  buttonText: string;
  heading: string;
  content: string;
  bookingIdHash: string;
  bookingLanguage: string;
};

const AdditionalGuestsLink = ({
  buttonText,
  heading,
  content,
  bookingIdHash,
  bookingLanguage,
}: AdditionalGuestsProps): JSX.Element => {
  const intl = useIntl();
  const header =
    translateProp(intl, heading) ||
    intl.formatMessage(additionalGuestMessages.BEFORE_YOU_ARRIVE);
  const message = translateProp(intl, content);
  const buttonTxt =
    translateProp(intl, buttonText) ||
    intl.formatMessage(additionalGuestMessages.COMPLETE_ADDITIONAL_INFORMATION);
  return (
    <>
      <hr className="separator" />
      <HeaderStyle data-testid="gog-section-heading">{header}</HeaderStyle>
      {message ? (
        <ContentStyle
          data-testid="confirmation-content"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(message),
          }} /* // nosemgrep */
        />
      ) : (
        <ContentStyle data-testid="confirmation-content">
          <FormattedMessage
            defaultMessage="In order to tailor our experiences to our guests'' preferences <b>we need some additional information from all participating guests.</b>"
            description="Additional guests information content message"
            values={{
              b: (chunks: React.ReactNode) => <b>{chunks}</b>,
            }}
          />
        </ContentStyle>
      )}
      <AdditionalInfoButton
        className="GoGAdditionalInfoButton brand-bg"
        href={`/guest-information/${bookingIdHash}?lang=${bookingLanguage}`}
        target="_blank"
        data-testid="guest-information-page-btn"
      >
        {buttonTxt}
        <ScreenReader className="screenreader-text">
          (Link opens in a new tab)
        </ScreenReader>
      </AdditionalInfoButton>
    </>
  );
};

export default AdditionalGuestsLink;
